import "../components/cpl-code-editor";
import "../components/run-history-export";
import {
  EXECUTION_TIMEOUT_SECONDS,
  executionTimeoutVariables,
} from "../execution-limits";
import { renderUsage } from "../components/codex-usage";
import { ApiError, executionError } from "../api/errors";
import { TableColumns } from "../components/table-columns";
import { LitElement, html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import {
  api,
  executionModeLabel,
  type CodexResponse,
  type CodexProtocol,
  type CodexConfigSource,
  type CodexAudit,
  type CodexRun,
  type CodexModel,
  type SkillRecord,
} from "../api/client";
import type { CodexEnvEditor } from "../components/codex-env-editor";
import "../components/codex-env-editor";
import "../components/app-dialog";
import "../components/app-pagination";
import "../components/codex-run-events";
import {
  applyKnowledgeStop,
  isExecutionStopped,
} from "../components/knowledge-run-stop";
import type { KnowledgeUpdateStop } from "../api/client";
import { i18n } from "../i18n";
import { waitForLoadingCycle } from "../components/table-loading";
import "../components/a2k-skill-multiselect";
import type { A2kSkillMultiselect } from "../components/a2k-skill-multiselect";
import {
  configPayload,
  configSourceField,
  configSourceLabel,
} from "../components/codex-config-source";

const PROTOCOL_URLS: Record<CodexProtocol, string> = {
  openai: "https://api.openai.com/v1",
  anthropic: "https://api.anthropic.com/v1/messages",
};

@customElement("codex-view")
export class CodexView extends LitElement {
  private pollTimer?: number;
  private runsVersion = 0;
  private modelsVersion = 0;
  private modelChosen = false;
  private readonly columns = new TableColumns(this);
  @state() private promptText = "";
  @state() private systemPromptText = "";
  @state() private protocol: CodexProtocol = "openai";
  @state() private configSource: CodexConfigSource = "custom";
  @state() private effort = "medium";
  @state() private baseUrl = PROTOCOL_URLS.openai;
  @state() private model = "custom";
  @state() private customModel = "";
  @state() private models: CodexModel[] = [];
  @state() private modelsBusy = false;
  @state() private modelsError = "";
  @state() private response: CodexResponse | null = null;
  @state() private error = "";
  @state() private failureRequestId = "";
  @state() private hasExecuted = false;
  @state() private busy = false;
  @state() private skills: SkillRecord[] = [];
  @state() private selectedSkills: string[] = [];
  @state() private runs: CodexRun[] = [];
  @state() private runsBusy = false;
  @state() private runsError = "";
  @state() private runPage = 1;
  @state() private runPageSize = 10;
  @state() private runTotalPages = 0;
  @state() private runStatus = "";
  @state() private runModel = "";
  @state() private runEffort = "";
  @state() private runAvailabilityProbe = "";
  @state() private runFrom = "";
  @state() private runTo = "";
  @state() private selectedRun: CodexRun | null = null;
  @state() private detailBusy = false;
  @state() private detailError = "";

  protected createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("languagechange", this.languageChanged);
    void api
      .skills()
      .then((result) => {
        this.skills = result.items;
      })
      .catch(() => undefined);
    void this.loadRuns();
    void this.loadModels();
    this.schedulePoll();
  }

  disconnectedCallback() {
    window.clearTimeout(this.pollTimer);
    this.runsVersion++;
    this.modelsVersion++;
    window.removeEventListener("languagechange", this.languageChanged);
    super.disconnectedCallback();
  }

  private schedulePoll() {
    this.pollTimer = window.setTimeout(async () => {
      try {
        if (this.isConnected && !document.hidden && !this.runsBusy) {
          await this.loadRuns(this.runPage, true);
          const id = this.selectedRun?.id;
          if (
            id &&
            ["running", "queued"].includes(this.selectedRun?.status || "") &&
            !this.detailBusy
          ) {
            const result = await api.codexRun(id);
            if (this.isConnected && this.selectedRun?.id === id) {
              this.selectedRun = result.run;
              this.detailError = "";
            }
          }
        }
      } catch (error) {
        this.detailError = (error as Error).message;
      } finally {
        if (this.isConnected) this.schedulePoll();
      }
    }, 3000);
  }

  private languageChanged = () => this.requestUpdate();

  private async loadModels() {
    const version = ++this.modelsVersion;
    this.modelsBusy = true;
    this.modelsError = "";
    try {
      const result = await api.codexModels();
      if (!this.isConnected || version !== this.modelsVersion) return;
      this.models = result.models;
      if (
        this.configSource === "custom" &&
        this.protocol === "openai" &&
        !this.modelChosen
      ) {
        this.model = this.models[0]?.value || "custom";
      } else if (
        this.model &&
        this.model !== "custom" &&
        !this.models.some((item) => item.value === this.model)
      ) {
        this.customModel = this.model;
        this.model = "custom";
      }
    } catch (error) {
      if (!this.isConnected || version !== this.modelsVersion) return;
      this.modelsError = (error as Error).message;
    } finally {
      if (version === this.modelsVersion) this.modelsBusy = false;
    }
  }

  private changeProtocol(protocol: CodexProtocol) {
    const current = this.baseUrl.trim().replace(/\/+$/, "");
    if (!current || Object.values(PROTOCOL_URLS).includes(current)) {
      this.baseUrl = PROTOCOL_URLS[protocol];
    }
    this.protocol = protocol;
    if (protocol === "anthropic") {
      this.modelChosen = true;
      this.model = "custom";
    }
  }

  private changeConfigSource(value: CodexConfigSource) {
    this.configSource = value;
    this.modelChosen = value === "local";
    this.model = value === "local" ? "" : this.models[0]?.value || "custom";
    this.effort = value === "local" ? "" : "medium";
  }

  private providerAdjustment(audit?: CodexAudit | null) {
    const adapter = audit?.provider_adapter;
    if (!adapter) return nothing;
    return html`<dt>${i18n.t("codexRequestedEffort")}</dt>
      <dd>${adapter.requested_effort}</dd>
      <dt>${i18n.t("codexEffectiveEffort")}</dt>
      <dd>${adapter.effective_effort ?? i18n.t("codexEffortOmitted")}</dd>
      ${
        adapter.adjustments?.length
          ? html`<dt>${i18n.t("codexEffortAdjustments")}</dt>
              <dd>
                ${adapter.adjustments.map((item) => html`<p>${item.message}</p>`)}
              </dd>`
          : nothing
      }`;
  }

  private async submit(event: SubmitEvent) {
    event.preventDefault();
    this.busy = true;
    this.error = "";
    this.failureRequestId = "";
    const values: Record<string, unknown> = Object.fromEntries(
      new FormData(event.currentTarget as HTMLFormElement),
    );

    try {
      // Read editor values directly as well as their hidden FormData mirrors:
      // a submit in the same input tick can precede Lit's next render.
      for (const name of ["prompt", "system_prompt"]) {
        const editor = this.querySelector<HTMLElement & { value: string }>(
          `cpl-code-editor[data-field="${name}"]`,
        );
        if (typeof editor?.value === "string") values[name] = editor.value;
      }
      if (!String(values.prompt || "").trim()) {
        this.querySelector<HTMLElement>(
          'cpl-code-editor[data-prompt="required"]',
        )?.focus();
        throw new Error(i18n.t("formFieldsRequired"));
      }
      values.protocol = this.protocol;
      values.skills =
        this.querySelector<A2kSkillMultiselect>(
          "a2k-skill-multiselect",
        )?.readValue() || [];
      const environment =
        this.querySelector<CodexEnvEditor>("codex-env-editor")?.readValue();
      if (environment === null || environment === undefined) return;
      values.envs = environment;
      this.response = await api.codex(configPayload(this.configSource, values));
      this.hasExecuted = true;
    } catch (error) {
      this.error = (error as Error).message;
      this.failureRequestId =
        error instanceof ApiError ? error.requestId || "" : "";
      this.response = null;
      this.hasExecuted = true;
    } finally {
      this.busy = false;
      if (this.hasExecuted) void this.loadRuns(1);
    }
  }

  private runFilters() {
    return {
      status: this.runStatus,
      model: this.runModel,
      effort: this.runEffort,
      is_availability_probe: this.runAvailabilityProbe || undefined,
      from: this.runFrom ? new Date(this.runFrom).toISOString() : undefined,
      to: this.runTo ? new Date(this.runTo).toISOString() : undefined,
    };
  }

  private async loadRuns(page = this.runPage, background = false) {
    const version = ++this.runsVersion;
    const loadingStarted = performance.now();
    if (!background) this.runsBusy = true;
    this.runsError = "";
    try {
      const result = await api.codexRuns({
        ...this.runFilters(),
        page,
        page_size: this.runPageSize,
      });
      if (!this.isConnected || version !== this.runsVersion) return;
      this.runs = result.items;
      this.runPage = result.page;
      this.runTotalPages = result.total_pages;
    } catch (error) {
      if (version !== this.runsVersion) return;
      if (background) {
        this.runsError = (error as Error).message;
        return;
      }
      this.runs = [];
      this.runPage = page;
      this.runTotalPages = 0;
      this.runsError = (error as Error).message;
    } finally {
      if (!background) await waitForLoadingCycle(loadingStarted);
      if (version === this.runsVersion) this.runsBusy = false;
    }
  }

  private async showRun(run: CodexRun) {
    this.selectedRun = run;
    this.detailBusy = true;
    this.detailError = "";
    try {
      const result = await api.codexRun(run.id);
      if (this.selectedRun?.id === run.id) this.selectedRun = result.run;
    } catch (error) {
      if (this.selectedRun?.id === run.id) {
        this.detailError = i18n.t("codexHistoryDetailError", {
          message: (error as Error).message,
        });
      }
    } finally {
      if (this.selectedRun?.id === run.id) this.detailBusy = false;
    }
  }

  private closeRun() {
    this.selectedRun = null;
    this.detailBusy = false;
    this.detailError = "";
  }

  private async deleteRun(run?: CodexRun) {
    if (!run) return;
    try {
      await api.deleteCodexRun(run.id);
      this.closeRun();
      await this.loadRuns(this.runPage);
    } catch (error) {
      this.runsError = (error as Error).message;
    }
  }

  private dateTime(value: string | null | undefined) {
    if (!value) return "-";
    const parsed = new Date(value);
    return Number.isNaN(parsed.valueOf()) ? value : parsed.toLocaleString();
  }

  private runOutput(run: CodexRun) {
    return run.output ?? run.text ?? "";
  }

  private failureSummary(run: CodexRun) {
    if (isExecutionStopped(run)) return nothing;
    if (run.status !== "failed" && !run.error) return nothing;
    return html`<small class="codex-failure-reason">
      ${executionError(run.error) || i18n.t("codexMissingFailureReason")}
    </small>`;
  }

  private runStatusText(run: CodexRun) {
    if (isExecutionStopped(run)) return i18n.t("knowledgeUpdateStopped");
    if (
      ["queued", "running"].includes(run.status) &&
      (run.stopping || run.stop_requested)
    ) {
      return i18n.t("knowledgeUpdateStopping");
    }
    return run.status;
  }

  private stopState(event: CustomEvent<KnowledgeUpdateStop>) {
    const result = event.detail;
    this.runs = this.runs.map((run) =>
      run.id === result.request_id ? applyKnowledgeStop(run, result) : run,
    );
    if (this.selectedRun?.id === result.request_id)
      this.selectedRun = applyKnowledgeStop(this.selectedRun, result);
  }

  private runDetail() {
    const run = this.selectedRun;
    if (!run) return nothing;
    if (this.detailBusy) {
      return html`<p class="codex-run-detail-loading">${i18n.t("loading")}</p>`;
    }
    return html`<div class="codex-run-detail">
      <knowledge-run-stop
        .run=${run}
        @stop-state=${(event: CustomEvent<KnowledgeUpdateStop>) => this.stopState(event)}
      ></knowledge-run-stop>
      ${isExecutionStopped(run) ? html`<p role="status">${i18n.t("knowledgeUpdateStopped")}</p>` : nothing}
      <codex-run-events
        .runId=${run.id}
        .active=${["running", "queued"].includes(run.status)}
      ></codex-run-events>
      ${
        !isExecutionStopped(run) && (run.status === "failed" || run.error)
          ? html`<section class="status status-error" role="alert">
              <strong>${i18n.t("codexHistoryError")}</strong>
              <pre class="code-output">
${executionError(run.error) || i18n.t("codexMissingFailureReason")}</pre>
            </section>`
          : nothing
      }
      <section class="output-section">
        <strong>${i18n.t("codexHistoryRequest")}</strong>
        <pre class="code-output codex-history-output">${run.prompt || "-"}</pre>
      </section>
      <section class="output-section">
        <strong>${i18n.t("codexHistorySystemPrompt")}</strong>
        <pre class="code-output codex-history-output">
${run.system_prompt || "-"}</pre>
      </section>
      <section class="output-section">
        <strong>${i18n.t("codexHistoryOutput")}</strong>
        <pre class="code-output codex-history-output">
${this.runOutput(run) || "-"}</pre>
      </section>
      <section class="output-section">
        <strong>Request / response audit</strong>
        <pre class="code-output codex-history-output">
${JSON.stringify(run.audit || {}, null, 2)}</pre>
      </section>
      <dl class="detail-list codex-run-detail-list">
        <dt>${i18n.t("codexResultRequestId")}</dt>
        <dd><code>${run.id}</code></dd>
        <dt>${i18n.t("codexHistoryStatus")}</dt>
        <dd>${this.runStatusText(run)}</dd>
        <dt>${i18n.t("codexProtocol")}</dt>
        <dd>
          ${run.config_source === "local" ? i18n.t("codexConfigInherit") : run.protocol || "openai"}
        </dd>
        <dt>${i18n.t("codexConfigSource")}</dt>
        <dd>${configSourceLabel(run.config_source)}</dd>
        ${this.providerAdjustment(run.audit)}
        <dt>${i18n.t("codexResultModel")}</dt>
        <dd>${run.model || i18n.t("codexConfigInherit")}</dd>
        <dt>${i18n.t("codexResultEffort")}</dt>
        <dd>
          ${run.reasoning_effort || run.effort || i18n.t("codexConfigInherit")}
        </dd>
        <dt>${i18n.t("codexExecutionMode")}</dt>
        <dd>${executionModeLabel(run.execution_mode)}</dd>
        <dt>${i18n.t("codexResultUsage")}</dt>
        <dd>${renderUsage(run.normalized_usage)}</dd>
        <dt>${i18n.t("codexResultLatency")}</dt>
        <dd>${run.latency_ms ?? "-"}${run.latency_ms == null ? "" : " ms"}</dd>
        <dt>${i18n.t("codexHistorySkills")}</dt>
        <dd>${run.skills?.join(", ") || "-"}</dd>
        <dt>${i18n.t("codexHistoryEnvKeys")}</dt>
        <dd>${run.env_keys?.join(", ") || "-"}</dd>
        <dt>${i18n.t("codexHistoryWorkspace")}</dt>
        <dd><code>${run.workspace || "-"}</code></dd>
        <dt>${i18n.t("codexHistoryCreated")}</dt>
        <dd>${this.dateTime(run.created_at)}</dd>
        <dt>${i18n.t("codexHistoryError")}</dt>
        <dd>
          ${isExecutionStopped(run) ? i18n.t("knowledgeUpdateStopped") : executionError(run.error) || "—"}
        </dd>
      </dl>
    </div>`;
  }

  private runHistoryError() {
    if (!this.runsError) return nothing;
    return html`<p class="status status-error" role="alert">
      ${i18n.t("codexHistoryLoadError", { message: this.runsError })}
    </p>`;
  }

  private resultText(): string {
    if (this.error) {
      return i18n.t("codexError", { message: this.error });
    }
    return this.hasExecuted && this.response
      ? this.response.text
      : i18n.t("codexInitial");
  }

  render() {
    return html`
      <page-heading
        heading=${i18n.t("codexTitle")}
        description=${i18n.t("codexDescription")}
      ></page-heading>
      <surface-window heading=${i18n.t("codexConfiguration")}>
        <form class="form-grid" @submit=${this.submit}>
          ${configSourceField(this.configSource, (value) => this.changeConfigSource(value))}
          ${
            this.configSource === "custom"
              ? html`
                  <label>
                    ${i18n.t("codexProviderKey")}
                    <input
                      class="a2k-native-input"
                      name="apikey"
                      type="password"
                      autocomplete="off"
                      required
                    />
                  </label>
                  <label class="native-field">
                    <span>${i18n.t("codexProtocol")}</span>
                    <select
                      class="a2k-native-input"
                      name="protocol"
                      .value=${this.protocol}
                      @change=${(event: Event) => {
                        this.changeProtocol(
                          (event.target as HTMLSelectElement)
                            .value as CodexProtocol,
                        );
                      }}
                    >
                      <option value="openai">OpenAI</option>
                      <option value="anthropic">Anthropic</option>
                    </select>
                  </label>
                  <label class="native-field">
                    <span>${i18n.t("codexBaseUrl")}</span>
                    <input
                      class="a2k-native-input"
                      name="base_url"
                      required
                      .value=${this.baseUrl}
                      @input=${(event: Event) => {
                        this.baseUrl = (event.target as HTMLInputElement).value;
                      }}
                    />
                  </label>
                `
              : nothing
          }
          <label class="native-field">
            <span>${i18n.t("codexModel")}</span>
            <select
              class="a2k-native-input"
              name="model"
              .value=${this.model}
              @change=${(event: Event) => {
                this.modelChosen = true;
                this.model = (event.target as HTMLSelectElement).value;
              }}
            >
              ${
                this.configSource === "local"
                  ? html`<option value="">
                      ${i18n.t("codexConfigInherit")}
                    </option>`
                  : nothing
              }
              ${(this.configSource === "local" || this.protocol === "openai"
                ? this.models
                : []
              ).map(
                (model) =>
                  html`<option value=${model.value}>
                    ${model.label || model.value} (${model.value})
                  </option>`,
              )}
              <option value="custom">${i18n.t("codexCustomModel")}</option>
            </select>
          </label>
          ${
            this.protocol === "openai"
              ? html`<div class="wide" role="status">
                  ${this.modelsBusy ? i18n.t("loading") : nothing}
                  ${
                    this.modelsError ||
                    (!this.modelsBusy && !this.models.length)
                      ? html`<p>
                          ${
                            i18n.language === "zh-CN"
                              ? "模型目录暂不可用，可手动输入自定义模型。"
                              : "Model catalog unavailable. Enter a custom model."
                          }
                          ${this.modelsError}
                        </p>`
                      : nothing
                  }
                  <a2k-button
                    type="button"
                    ?disabled=${this.modelsBusy}
                    @click=${() => this.loadModels()}
                  >
                    ${i18n.t("refresh")}
                  </a2k-button>
                </div>`
              : nothing
          }
          ${
            this.model === "custom"
              ? html`<label class="native-field">
                  <span>${i18n.t("codexCustomModel")}</span>
                  <input
                    class="a2k-native-input"
                    name="custom_model"
                    required
                    .value=${this.customModel}
                    @input=${(event: Event) => {
                      this.modelChosen = true;
                      this.customModel = (
                        event.target as HTMLInputElement
                      ).value;
                    }}
                  />
                </label>`
              : nothing
          }
          <input type="hidden" name="execution_mode" value="unrestricted" />
          <label class="native-field">
            <span>${i18n.t("codexEffort")}</span>
            <select
              class="a2k-native-input"
              name="reasoning_effort"
              .value=${this.effort}
              @change=${(event: Event) => {
                this.effort = (event.target as HTMLSelectElement).value;
              }}
            >
              ${
                this.configSource === "local"
                  ? html`<option value="">
                      ${i18n.t("codexConfigInherit")}
                    </option>`
                  : nothing
              }
              <option value="low">low</option>
              <option value="medium">medium</option>
              <option value="high">high</option>
              <option value="xhigh">xhigh</option>
              <option value="max">max</option>
              <option value="ultra">ultra</option>
            </select>
          </label>
          <label class="native-field">
            <span
              >${i18n.t("codexTimeoutSeconds", executionTimeoutVariables)}</span
            >
            <input
              class="a2k-native-input"
              type="number"
              name="timeout_seconds"
              min="1"
              step="1"
              max=${EXECUTION_TIMEOUT_SECONDS}
              value=${EXECUTION_TIMEOUT_SECONDS}
              required
            />
          </label>
          <div class="wide skill-picker">
            <p class="field-help-static">${i18n.t("codexSkillsHelp")}</p>
            <a2k-skill-multiselect
              .skills=${this.skills.filter((skill) => skill.enabled)}
              .selected=${this.selectedSkills}
              @selection-change=${(event: CustomEvent<string[]>) => {
                this.selectedSkills = event.detail;
              }}
            ></a2k-skill-multiselect>
          </div>
          <label class="wide">
            ${i18n.t("codexSystemPrompt")}
            <cpl-code-editor
              data-field="system_prompt"
              language="markdown"
              .value=${this.systemPromptText}
              .label=${i18n.t("codexSystemPrompt")}
              .minHeight=${"8rem"}
              .disabled=${this.busy}
              @value-change=${(event: CustomEvent<{ value: string }>) => {
                this.systemPromptText = event.detail.value;
              }}
            ></cpl-code-editor>
            <input
              type="hidden"
              name="system_prompt"
              .value=${this.systemPromptText}
            />
          </label>
          <label class="wide">
            ${i18n.t("codexPrompt")}
            <cpl-code-editor
              data-prompt="required"
              data-field="prompt"
              language="markdown"
              .value=${this.promptText}
              .label=${i18n.t("codexPrompt")}
              .minHeight=${"14rem"}
              .disabled=${this.busy}
              @value-change=${(event: CustomEvent<{ value: string }>) => {
                this.promptText = event.detail.value;
              }}
            ></cpl-code-editor>
            <input type="hidden" name="prompt" .value=${this.promptText} />
          </label>
          <codex-env-editor class="wide"></codex-env-editor>
          <a2k-button type="submit" ?disabled=${this.busy}>
            ${this.busy ? i18n.t("working") : i18n.t("codexExecute")}
          </a2k-button>
        </form>
      </surface-window>
      <surface-window heading=${i18n.t("codexResult")}>
        <pre
          class="code-output codex-response-text ${
            this.error ? "status-error" : ""
          }"
        >
${this.resultText()}</pre>
        ${
          this.failureRequestId
            ? html`
                <p>
                  ${i18n.t("codexResultRequestId")}:
                  <code>${this.failureRequestId}</code>
                </p>
              `
            : nothing
        }
        ${
          this.response
            ? html`<dl class="detail-list codex-response-meta">
                <dt>${i18n.t("codexProtocol")}</dt>
                <dd>
                  ${
                    this.response.config_source === "local"
                      ? i18n.t("codexConfigLocal")
                      : this.response.protocol || "openai"
                  }
                </dd>
                ${this.providerAdjustment(this.response.audit)}
                <dt>${i18n.t("codexResultModel")}</dt>
                <dd>${this.response.model || i18n.t("codexConfigInherit")}</dd>
                <dt>${i18n.t("codexResultEffort")}</dt>
                <dd>
                  ${this.response.reasoning_effort || this.response.effort || i18n.t("codexConfigInherit")}
                </dd>
                <dt>${i18n.t("codexExecutionMode")}</dt>
                <dd>${this.response.execution_mode}</dd>
                <dt>${i18n.t("codexHistoryStatus")}</dt>
                <dd>${this.response.execution_status}</dd>
                <dt>${i18n.t("codexResultUsage")}</dt>
                <dd>${renderUsage(this.response.normalized_usage)}</dd>
                <dt>${i18n.t("codexResultLatency")}</dt>
                <dd>${this.response.latency_ms} ms</dd>
                <dt>${i18n.t("codexResultRequestId")}</dt>
                <dd><code>${this.response.request_id}</code></dd>
                <dt>${i18n.t("codexHistoryWorkspace")}</dt>
                <dd><code>${this.response.workspace}</code></dd>
              </dl>`
            : null
        }
      </surface-window>
      <surface-window heading=${i18n.t("codexHistoryTitle")}>
        <run-history-export
          kind="codex"
          .filters=${this.runFilters()}
        ></run-history-export>
        <form
          class="codex-history-filters"
          @submit=${(event: SubmitEvent) => {
            event.preventDefault();
            void this.loadRuns(1);
          }}
        >
          <label class="native-field"
            ><span>${i18n.t("codexHistoryStatus")}</span>
            <select
              class="a2k-native-input"
              .value=${this.runStatus}
              @change=${(event: Event) => (this.runStatus = (event.target as HTMLSelectElement).value)}
            >
              <option value="">${i18n.t("codexHistoryAll")}</option>
              <option value="running">${i18n.t("codexHistoryRunning")}</option>
              <option value="success">
                ${i18n.t("codexHistorySucceeded")}
              </option>
              <option value="failed">${i18n.t("codexHistoryFailed")}</option>
            </select>
          </label>
          <label class="native-field"
            ><span>${i18n.t("codexModel")}</span>
            <input
              class="a2k-native-input"
              .value=${this.runModel}
              @input=${(event: Event) => (this.runModel = (event.target as HTMLInputElement).value)}
            />
          </label>
          <label class="native-field"
            ><span>${i18n.t("codexEffort")}</span>
            <select
              class="a2k-native-input"
              .value=${this.runEffort}
              @change=${(event: Event) => (this.runEffort = (event.target as HTMLSelectElement).value)}
            >
              <option value="">${i18n.t("codexHistoryAll")}</option>
              ${["low", "medium", "high", "xhigh", "max", "ultra"].map(
                (effort) => html`<option value=${effort}>${effort}</option>`,
              )}
            </select>
          </label>
          <label class="native-field"
            ><span>${i18n.t("codexHistoryAvailabilityProbe")}</span>
            <select
              class="a2k-native-input"
              name="is_availability_probe"
              .value=${this.runAvailabilityProbe}
              @change=${(event: Event) => {
                this.runAvailabilityProbe = (
                  event.target as HTMLSelectElement
                ).value;
                void this.loadRuns(1);
              }}
            >
              <option value="">${i18n.t("codexHistoryAll")}</option>
              <option value="true">${i18n.t("codexHistoryProbeYes")}</option>
              <option value="false">${i18n.t("codexHistoryProbeNo")}</option>
            </select>
          </label>
          <label class="native-field" title=${i18n.t("codexHistoryTimeHelp")}
            ><span>${i18n.t("codexHistoryFrom")}</span>
            <input
              class="a2k-native-input"
              type="datetime-local"
              step="1"
              name="started_from"
              .value=${this.runFrom}
              @change=${(event: Event) => {
                this.runFrom = (event.target as HTMLInputElement).value;
                void this.loadRuns(1);
              }}
            />
          </label>
          <label class="native-field" title=${i18n.t("codexHistoryTimeHelp")}
            ><span>${i18n.t("codexHistoryTo")}</span>
            <input
              class="a2k-native-input"
              type="datetime-local"
              step="1"
              name="started_to"
              .value=${this.runTo}
              @change=${(event: Event) => {
                this.runTo = (event.target as HTMLInputElement).value;
                void this.loadRuns(1);
              }}
            />
          </label>
          <label class="native-field codex-history-page-size"
            ><span>${i18n.t("codexHistoryPageSize")}</span>
            <select
              class="a2k-native-input"
              .value=${String(this.runPageSize)}
              @change=${(event: Event) => {
                this.runPageSize = Number(
                  (event.target as HTMLSelectElement).value,
                );
                void this.loadRuns(1);
              }}
            >
              ${[10, 20, 50, 100].map((size) => html`<option value=${size}>${size}</option>`)}
            </select>
          </label>
          <a2k-button type="submit" ?disabled=${this.runsBusy}
            >${i18n.t("codexHistoryFilter")}</a2k-button
          >
        </form>
        ${this.runHistoryError()}
        <div class="codex-history-table">
          <table class="data-table">
            <thead>
              <tr>
                <th>${i18n.t("codexHistoryCreated")}</th>
                <th>${i18n.t("codexHistoryStatus")}</th>
                <th>${i18n.t("codexConfigSource")}</th>
                <th>${i18n.t("codexModel")}</th>
                <th class="codex-optional-column">${i18n.t("codexEffort")}</th>
                <th class="codex-optional-column">
                  ${i18n.t("codexResultLatency")}
                </th>
                <th>${i18n.t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              ${
                this.runsBusy
                  ? html`<tr>
                      <td colspan="7">
                        <table-loading
                          label=${i18n.t("loading")}
                        ></table-loading>
                      </td>
                    </tr>`
                  : this.runs.length
                    ? this.runs.map(
                        (run) =>
                          html`<tr>
                            <td>${this.dateTime(run.created_at)}</td>
                            <td>
                              ${this.runStatusText(run)}${this.failureSummary(run)}
                              <small class="codex-failure-reason"
                                >${run.progress?.latest?.slice(0, 180) || ""}</small
                              >
                            </td>
                            <td>
                              ${configSourceLabel(run.config_source)}${
                                run.config_source === "local"
                                  ? nothing
                                  : html` · ${run.protocol || "openai"}`
                              }
                            </td>
                            <td>
                              ${run.model || i18n.t("codexConfigInherit")}
                            </td>
                            <td class="codex-optional-column">
                              ${run.effort || i18n.t("codexConfigInherit")}
                            </td>
                            <td class="codex-optional-column">
                              ${run.latency_ms ?? "-"}
                            </td>
                            <td>
                              <knowledge-run-stop
                                .run=${run}
                                @stop-state=${(event: CustomEvent<KnowledgeUpdateStop>) => this.stopState(event)}
                              ></knowledge-run-stop>
                              <a2k-button
                                type="button"
                                @click=${() => void this.showRun(run)}
                                >${i18n.t("details")}</a2k-button
                              >
                              <a2k-button
                                type="button"
                                ?disabled=${run.status === "running" || run.status === "queued"}
                                @click=${() => void this.deleteRun(run)}
                              >
                                ${i18n.t("remove")}
                              </a2k-button>
                            </td>
                          </tr>`,
                      )
                    : html`<tr>
                        <td colspan="7" class="empty-cell">
                          ${i18n.t("codexHistoryEmpty")}
                        </td>
                      </tr>`
              }
            </tbody>
          </table>
        </div>
        <app-pagination
          .page=${this.runPage}
          .totalPages=${this.runTotalPages}
          .busy=${this.runsBusy}
          @page-change=${(event: CustomEvent<{ page: number }>) => void this.loadRuns(event.detail.page)}
        ></app-pagination>
      </surface-window>
      <app-dialog
        .open=${Boolean(this.selectedRun)}
        heading=${i18n.t("codexHistoryDetail")}
        cancel-label=${i18n.t("close")}
        .hideConfirm=${true}
        .message=${this.detailError}
        @dialog-cancel=${this.closeRun}
      >
        ${this.runDetail()}
      </app-dialog>
    `;
  }
}
